import React from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { styled } from '@mui/material/styles';
import {
  Dialog,
  DialogTitle,
  DialogActions,
  DialogContent,
  Button,
  Typography,
  Box,
} from '@mui/material';
import {
  DataGridPro,
  GridCallbackDetails,
  GridPaginationModel,
  GridRowSelectionModel,
} from '@mui/x-data-grid-pro';
import SearchIcon from '@mui/icons-material/Search';

import { useAppSelector } from '../../../hooks/useAppSelector';
import { useAppDispatch } from '../../../hooks/useAppDispatch';

import { Provider } from '../../../data/models/provider.model';
import { SortModel } from '../../../data/models/sort-model.model';
import { AssignTripLeg } from '../../../data/models/trip-leg-assign.model';
import { TripLeg } from '../../../data/models/reservation.model';

import * as accountingSlice from '../../../data/accounting.slice';
import * as providerSlice from '../../../data/provider.slice';
import * as triplegSlice from '../../../data/tripleg.slice';
import * as uiSlice from '../../../data/ui.slice';
// import TextInput from '../../forms/TextInput';
import TextSearch from '../../controls/TextSearch';
import SelectMenu from '../../forms/SelectMenu';

interface TripLegPackageDialogProps {
  tripLegs: TripLeg[];
  open: boolean;
  close: () => void;
}

const PREFIX = 'TripLegPackageDialog';

const classes = {
  content: `${PREFIX}-content`,
  left: `${PREFIX}-left`,
  right: `${PREFIX}-right`,
};

const RootDialog = styled(Dialog)(({ theme }) => ({
  [`& .${classes.content}`]: {
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing(2),
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },

  [`& .${classes.left}`]: {
    minWidth: 500,
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },

  [`& .${classes.right}`]: {
    marginTop: theme.spacing(5),
    minWidth: 500,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
}));

export default function TripLegPackageDialog({
  tripLegs,
  open,
  close,
}: TripLegPackageDialogProps) {
  const { t } = useTranslation('common');
  const dispatch = useAppDispatch();
  const requiredFieldMessage = t('common.requiredField');

  const loadingProviders = useAppSelector(
    providerSlice.selectLoadingProviderList,
  );
  const providers = useAppSelector(providerSlice.selectProviderList);
  const transportationServices = useAppSelector(
    accountingSlice.selectTransportationServices,
  );
  const savedTripLeg = useAppSelector(triplegSlice.selectSavedTripLeg);
  const saveSuccessMessage = useAppSelector(uiSlice.selectSuccessMessage);
  const [page, setPage] = React.useState(1);
  const [pageSize, setPageSize] = React.useState(5);
  const selectedFilter = 'ctt0&active0'; // only get cleared and active providers
  const [searchFilter, setSearchFilter] = React.useState('');
  const sortModel: SortModel = {
    column: 'transportationProviderName',
    direction: 'asc',
  };
  const [selectedProvider, setSelectedProvider] = React.useState<Provider>();
  const [requireProviderInfo, setRequireProviderInfo] = React.useState(false);

  const columns = [
    {
      field: 'transportationProviderName',
      headerName: t('triplegInventory.name') || '',
      flex: 2,
    },
  ];

  const transportationProviders = tripLegs.reduce<{}[]>(
    (providers, tripLeg) => {
      if (tripLeg.transportationProviders) {
        return [...providers, ...tripLeg.transportationProviders];
      }
      return providers;
    },
    [],
  );

  const formHook = useForm({
    defaultValues: {
      // tripLegReferenceID: tripLeg.tripLegReferenceID,
      // passengerUUID: tripLeg.passengerUUID,
    },
  });

  const handleClose = () => {
    formHook.reset();
    // setSelectedProvider(undefined);
    setRequireProviderInfo(false);
    close();
  };

  const onSearchChange = (val: string) => {
    setSearchFilter(val);
  };

  const onPageChange = (pg: number) => {
    setPage(pg);
  };

  const onPageSizeChange = (pgSize: number) => {
    setPageSize(pgSize);
  };

  const paginationChange = (
    model: GridPaginationModel,
    _details: GridCallbackDetails,
  ) => {
    if (model.page !== providers.currentPage - 1) {
      onPageChange(model.page + 1);
    }
    if (model.pageSize !== pageSize) {
      onPageSizeChange(model.pageSize);
    }
  };

  // const selectRow2 = (row: GridRowSelectionModel) => {
  //   const providerId = row ? (row[0] as number) : undefined;
  //   const provider = providers?.items.find(
  //     (p) => p.transportationProviderID === providerId,
  //   );
  //   setSelectedProvider(provider);
  //   const tpIndex =
  //     transportationProviders?.findIndex(
  //       (tp) => tp.transportationProviderID === providerId,
  //     ) || -1;
  //   setRequireProviderInfo(tpIndex < 0);
  // };

  const selectRow = (row: GridRowSelectionModel) => {
    const id = row[0];
    const provider = providers.items?.find(
      (p) => p.transportationProviderID === id,
    );
    setSelectedProvider(provider);
    setRequireProviderInfo(provider?.transportationProviderID ? true : false);
  };

  const handleSave = (data: any) => {
    for (let i = 0; i < tripLegs.length; i++) {
      const assignInfo: AssignTripLeg = {
        passengerUUID: tripLegs[i].passengerUUID,
        tripLegReferenceID: tripLegs[i].tripLegReferenceID!,
        transportationProviderID: selectedProvider!.transportationProviderID,
        providerTransportationServiceUUID: data.ProviderTransportationService,
        coverageAreaID: data.CoverageArea || undefined,
      };
      dispatch(triplegSlice.allocateTripLegToProviderAsync(assignInfo));
      close();
    }
  };

  React.useEffect(() => {
    dispatch(
      providerSlice.fetchProviderListAsync({
        pageSize,
        page,
        filter: selectedFilter,
        search: searchFilter,
        sortModel,
      }),
    );
    dispatch(accountingSlice.fetchTransportationServicesAsync());
  }, [searchFilter, page, pageSize]);

  return (
    <>
      <RootDialog open={open} onClose={handleClose} maxWidth={'xl'}>
        <DialogTitle>{t('triplegInventory.packageTitle')}</DialogTitle>
        <DialogContent className={classes.content}>
          <Box className={classes.content}>
            <Box className={classes.left}>
              <Typography>{t('triplegInventory.provider')}</Typography>
              <TextSearch
                id="text-search"
                icon={<SearchIcon />}
                label={t('triplegInventory.search')}
                placeholder={t('triplegInventory.searchProvider')}
                currentSearchFilter={searchFilter}
                onSearchChange={onSearchChange}></TextSearch>

              <DataGridPro
                getRowId={(row: any) => row.transportationProviderID}
                columns={columns}
                rows={providers.items}
                rowCount={providers.itemCount}
                paginationModel={{ page: providers.currentPage - 1, pageSize }}
                autoHeight={true}
                paginationMode="server"
                sortingMode="server"
                pagination={true}
                pageSizeOptions={[5]}
                onPaginationModelChange={paginationChange}
                loading={loadingProviders}
                checkboxSelection={false}
                disableColumnMenu={true}
                disableColumnSelector={true}
                disableMultipleRowSelection={true}
                onRowSelectionModelChange={selectRow}
              />
            </Box>
            <Box className={classes.right}>
              <SelectMenu
                label={t('triplegInventory.providerService')}
                name="ProviderTransportationService"
                values={transportationServices}
                valueAttr="transportationServiceUUID"
                labelAttr="name"
                control={formHook.control}
                required={requireProviderInfo}
                rules={{
                  required: requireProviderInfo
                    ? requiredFieldMessage
                    : undefined,
                }}
              />
              <SelectMenu
                label={t('triplegInventory.coverageArea')}
                name="CoverageArea"
                values={
                  selectedProvider?.transportationProviderCoverageAreas?.map(
                    (ca) => ca.coverageArea,
                  ) || []
                }
                valueAttr="coverageAreaID"
                labelAttr="coverageAreaName"
                control={formHook.control}
                required={requireProviderInfo}
                rules={{
                  required: requireProviderInfo
                    ? requiredFieldMessage
                    : undefined,
                }}
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" color="secondary" onClick={handleClose}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="contained"
            color="primary"
            disabled={!formHook.formState.isValid || !selectedProvider}
            onClick={formHook.handleSubmit(handleSave)}>
            {t('common.ok')}
          </Button>
        </DialogActions>
      </RootDialog>
    </>
  );
}
